import { useRef, useState } from 'react'
import classNames from 'classnames'
import { ShoppingAddToBag } from 'src/components/Icons/ShoppingAddToBagIcon'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { useMobile } from 'src/hooks/useMobile'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import ModalGiftCard from 'src/components/GiftCard/components/Modals/ModalGiftCard'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { isPreSale } from 'src/utils/pdp/isPreSale'
import ModalBlockAddToCart from 'src/components/common/Modal/ModalBlockAddToCart'

import type { ProductItem } from '../types/productItem'
import type { Product } from '../types/product'
import {
  useAddToCartLogic,
  useSkuSelectionModal,
  useBuyButtonHandler,
  useClearCartHandler,
} from '../ProductCard/hooks'
import { isGiftCard } from '../ProductCard/utils/giftCardUtils'

interface ProductAddToCartV3Props {
  product: Product
  currentSku: ProductItem
  Images: ProductItem['images']
  origin?: 'cart-feedback' | 'product-card'
  title?: string
  customFnOnAdd?: (e: React.MouseEvent<HTMLButtonElement>) => void
  eventSource?: string
}

const DefaultCtaV3 = ({
  handleBuyButton,
  handleClearCartAndContinue,
  openModalGiftCard,
  setOpenModalGiftCard,
  openModalPreSale,
  setOpenModalPreSale,
  label,
  isDisabled,
}: {
  handleBuyButton: (e: React.MouseEvent<HTMLButtonElement>) => void
  handleClearCartAndContinue: (e: React.MouseEvent<HTMLButtonElement>) => void
  openModalGiftCard: boolean
  setOpenModalGiftCard: React.Dispatch<React.SetStateAction<boolean>>
  setOpenModalPreSale: React.Dispatch<React.SetStateAction<boolean>>
  openModalPreSale: boolean
  label?: string
  isDisabled?: boolean
}) => {
  return (
    <>
      <button
        data-testid="product-card-add-to-cart"
        className={classNames(
          'z-10 product-card-add-to-cart-v3 h-10 w-10 md:h-[65px] md:w-[65px] absolute bottom-2 right-2 rounded-round bg-white text-restructure-primary flex items-center justify-center gap-2 p-3 md:p-5 border border-gray shadow-product-card-cta-v3 hover:opacity-95 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed'
        )}
        onClick={(e) => handleBuyButton(e)}
        type="button"
        aria-label={label ?? ''}
        disabled={isDisabled}
      >
        <ShoppingAddToBag color="#101010" width={25} height={24} />
      </button>

      <ModalGiftCard
        openModalGiftCard={openModalGiftCard}
        setOpenModalGiftCard={setOpenModalGiftCard}
        mainMessage="Nao e possivel comprar um Cartao Presente junto com outros produtos."
        subMessage="Deseja limpar seu carrinho para adicionar este produto ao carrinho?"
        buttonLabel="Limpar e adicionar ao carrinho"
        cancelLabel="Cancelar"
        onClearCart={handleClearCartAndContinue}
      />
      <ModalBlockAddToCart
        openModal={openModalPreSale}
        setOpenModal={setOpenModalPreSale}
        mainMessage="Não é possivel comprar um produto em pré-venda junto com outros produtos."
        subMessage="Deseja limpar seu carrinho para continuar com a compra?"
        buttonLabel="Limpar carrinho e continuar"
        cancelLabel="Cancelar"
        onClearCart={handleClearCartAndContinue}
      />
    </>
  )
}

const CartFeedbackCtaV3 = ({
  handleBuyButton,
  isDisabled,
}: {
  handleBuyButton: (e: React.MouseEvent<HTMLButtonElement>) => void
  isDisabled?: boolean
}) => {
  return (
    <button
      className="flex items-center justify-center bg-primaryBlue min-w-8 min-h-8 w-8 h-8 rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
      onClick={(e) => handleBuyButton(e)}
      type="button"
      aria-label="Adicionar produto ao carrinho"
      disabled={isDisabled}
    >
      <ShoppingAddToBag width={14} height={14} />
    </button>
  )
}

export function ProductAddToCartV3({
  product,
  currentSku,
  Images,
  origin = 'product-card',
  eventSource,
  title,
  customFnOnAdd,
}: ProductAddToCartV3Props) {
  const {
    orderForm: { items },
    isLoading,
    clearCart,
  } = useCheckout()

  const { pageData } = useGlobalContext()

  const { isMobile } = useMobile()
  const standardObject = getStandardObject(pageData)
  const productObject = getProductQueryObject(currentSku)

  const [openModalPreSale, setOpenModalPreSale] = useState(false)
  const [openModalGiftCard, setOpenModalGiftCard] = useState(false)
  const cartWasClearedRef = useRef(false)

  const hasMultipleItems = product.items.length > 1
  const currentProductIsGiftCard = isGiftCard(product.productId)
  const cartContainsGiftCard = items?.some((item) =>
    isGiftCard(item?.productId)
  )

  const cartContainsPreSaleItem = items?.some((item) =>
    isPreSale(item.preSaleDate)
  )

  const { addToCartFn } = useAddToCartLogic({
    product,
    currentSku,
    cartWasClearedRef,
    title,
    customFnOnAdd,
    eventSource,
  })

  const { openSkuSelectionModal } = useSkuSelectionModal({
    product,
    currentSku,
    Images,
    isLoading,
    addToCartFn,
    isMobile,
    standardObject,
    productObject,
  })

  const handleBuyButton = useBuyButtonHandler({
    currentSku,
    hasMultipleItems,
    currentProductIsGiftCard,
    cartContainsGiftCard,
    cartContainsPreSaleItem,
    standardObject,
    productObject,
    openSkuSelectionModal,
    addToCartFn,
    setOpenModalGiftCard,
    setOpenModalPreSale,
    slug: product.linkText,
  })

  const handleClearCartAndContinue = useClearCartHandler({
    hasMultipleItems,
    currentSku,
    cartWasClearedRef,
    clearCart,
    setOpenModalGiftCard,
    setOpenModalPreSale,
    openSkuSelectionModal,
    addToCartFn,
    standardObject,
    productObject,
  })

  return origin === 'product-card' ? (
    <DefaultCtaV3
      handleBuyButton={handleBuyButton}
      handleClearCartAndContinue={handleClearCartAndContinue}
      openModalPreSale={openModalPreSale}
      setOpenModalPreSale={setOpenModalPreSale}
      openModalGiftCard={openModalGiftCard}
      setOpenModalGiftCard={setOpenModalGiftCard}
      label={`Adicionar ${currentSku.name} ao carrinho`}
      isDisabled={isLoading}
    />
  ) : (
    <CartFeedbackCtaV3
      handleBuyButton={handleBuyButton}
      isDisabled={isLoading}
    />
  )
}
